<!-- 
  Uso: PolyButton(@click="done" :leftIcon="'fa-regular fa-rocket-launch'" :rightIcon="'fa-regular fa-rocket-launch'" :disabled="false") {{ "Texto del boton" }}
 -->

<script setup lang='ts'>
import { computed } from 'vue';

interface PolyButton {
  buttonColor?: 'Primary' | 'Green' | 'Red' | 'Blue' | 'White' | 'Black' | 'Grey',
  buttonStyle?: 'Solid' | 'Outline' | 'Blank',
  leftIcon?: string,
  rightIcon?: string,
  disabled?: boolean,
  buttonClass?: string,
}
const props = withDefaults(defineProps<PolyButton>(), {
  buttonColor: 'Primary',
  buttonStyle: 'Solid',
  disabled: false
})
defineEmits<{
  (e: 'click', value: Event): void
}>()

const classObject = computed(() => ({
  //- Asigno la clase de Color
  primary: props.buttonColor === 'Primary',
  green: props.buttonColor === 'Green',
  red: props.buttonColor === 'Red',
  blue: props.buttonColor === 'Blue',
  white: props.buttonColor === 'White',
  black: props.buttonColor === 'Black',
  grey: props.buttonColor === 'Grey',
  //- Asigno la classe de Estilo
  solid: props.buttonStyle === 'Solid',
  outline: props.buttonStyle === 'Outline',
  blank: props.buttonStyle === 'Blank',
}))
</script>

<template>
  <button @click="$emit('click', $event)" :class="[classObject, buttonClass]" :disabled="props.disabled">
    <i v-if="props.leftIcon" :class="props.leftIcon"></i>
    <slot></slot>
    <i v-if="props.rightIcon" :class="props.rightIcon"></i>
  </button>
</template>

<style lang='less' scoped>
@import '../frontend/mobile/themevars.less';

button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.46em;
  font-size: 1em;
  box-sizing: border-box;
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
  outline: none;
  border: none;
  border-radius: 5px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 0.61em;
  transition: color 200ms linear;
  font-family: 'pmxDefault';
  min-width: 98px;
}

&[disabled] {
  cursor: not-allowed;
}

button.solid {
  &.primary {
    background-color: @emphasis;
    color: white;

    &:hover {
      background-color: @primaryColor;
      color: white;
    }

    &:active {
      background-color: @primaryDark;
      color: white;
    }

    &[disabled] {
      background-color: @gray100;
      color: @gray30;
    }
  }

  &.green {
    background-color: @feedbackGreen;
    color: white;

    &:hover {
      background-color: @feedbackGreen400;
      color: white;
    }

    &:active {
      background-color: @feedbackGreen600;
      color: white;
    }

    &[disabled] {
      background-color: @gray100;
      color: @gray30;
    }
  }

  &.red {
    background-color: @feedbackRed;
    color: white;

    &:hover {
      background-color: @feedbackRed350;
      color: white;
    }

    &:active {
      background-color: @feedbackRed500;
      color: white;
    }

    &[disabled] {
      background-color: @gray100;
      color: @gray30;
    }
  }

  &.white {
    background-color: white;
    color: @emphasis;

    &:hover {
      background-color: @primaryLight2;
      color: @emphasis;
    }

    &:active {
      background-color: @emphasis;
      color: white;
    }

    &[disabled] {
      background-color: @primaryDark2;
      color: @primaryDark;
    }
  }
}

button.outline {

  &.primary,
  &.green {
    background-color: transparent;
    color: @emphasis;
    border: 1px solid @emphasis;

    &:hover {
      background-color: #E8D9FF;
      color: @emphasis;
      border: 1px solid @emphasis;
    }

    &:active {
      background-color: #8A55FF;
      color: white;
      border: 1px solid #8A55FF;
    }

    &[disabled] {
      background-color: transparent;
      color: #D9D9D9;
      border: 1px solid #D9D9D9;
    }
  }

  &.red {
    background-color: transparent;
    color: @feedbackRed;
    border: 1px solid @feedbackRed;

    &:hover {
      background-color: @feedbackRed100;
      color: @feedbackRed;
      border: 1px solid @feedbackRed;
    }

    &:active {
      background-color: @feedbackRed;
      color: white;
    }

    &[disabled] {
      background-color: transparent;
      color: @gray30;
      border: 1px solid @gray30;
    }
  }

  &.grey {
    background-color: white;
    color: #545454;
    border: 1px solid @gray30;

    &:hover {
      background-color: @grey50;
      color: @gray700;
      border-color: @gray35;
    }

    &:active {
      background-color: @gray100;
      color: @gray700;
      border-color: @gray35;
    }

    &[disabled] {
      background-color: white;
      color: @gray30;
      border-color: @gray30;
    }
  }

  &.white {
    background-color: transparent;
    color: white;
    border: 1px solid white;

    &:hover {
      background-color: transparent;
      color: @primaryLight2;
      border: 1px solid @primaryLight2;
    }

    &:active {
      background-color: @emphasis;
      color: white;
    }

    &[disabled] {
      background-color: transparent;
      color: @primaryDark2;
      border: 1px solid @primaryDark2;
    }
  }

  // Botón "cancelar/conservar" del modal Crítico (ver ConfirmModal.vue) y otros usos donde una acción neutral no
  // debe competir visualmente con la acción principal violeta.
  &.grey {
    background-color: transparent;
    color: @gray40;
    border: 1px solid @gray30;

    &:hover {
      background-color: @gray100;
      color: @gray40;
      border: 1px solid @gray30;
    }

    &:active {
      background-color: @gray30;
      color: white;
    }

    &[disabled] {
      background-color: transparent;
      color: @gray30;
      border: 1px solid @gray30;
    }
  }
}

button.blank {
  background-color: transparent;
  &.primary {
    background-color: transparent;
    color: @emphasis;
    border: none;
  }

  &:hover {
    background-color: @primaryLight;
    color: @emphasis;
  }

  &:active {
    background-color: @primaryDark;
    color: white;
  }

  &[disabled] {
    background-color: transparent;
    color: @gray30;
    border: none;
  }

  &.green {
    // background-color: white;
    color: #3AA76D;
    border: none;

    &:hover {
      background-color: #DAF0E3;
      color: #3AA76D;
    }

    &:active {
      background-color: #3AA76D;
      color: white;
    }

    &[disabled] {
      background-color: white;
      color: @gray30;
      border: 1px solid @gray30;
    }
  }

  &.red {
    // background-color: white;
    color: #F33535;
    border: none;

    &:hover {
      background-color: #FADBD7;
      color: #F33535;
    }

    &:active {
      background-color: #F33535;
      color: white;
    }

    &[disabled] {
      background-color: white;
      color: @gray30;
      border: 1px solid @gray30;
    }
  }

  &.blue {
    // background-color: white;
    color: #276EF1;
    border: none;

    &:hover {
      background-color: #D4E2FC;
      color: #276EF1;
    }

    &:active {
      background-color: #276EF1;
      color: white;
    }

    &[disabled] {
      background-color: white;
      color: @gray30;
      border: 1px solid @gray30;
    }
  }

  &.grey {
    // background-color: white;
    color: @gray40;
    border: none;

    &:hover {
      // background-color: #D4E2FC;
      color: @primaryColor;
    }

    &:active {
      background-color: @primaryDark;
      color: white;
    }

    &[disabled] {
      color: @gray30;
    }
  }
}
</style>
